import { trackRegistration } from "@/services/analytics";
import {
  clearStoredAttribution,
  signupAttributionForRequest,
} from "@/lib/signup-attribution";
import {
  getCsrfToken,
  storeCsrfToken,
} from "@/services/api/csrf";
import type { User } from "@/features/auth/services/firebase";
import type { UserProfile } from "@/features/auth/contexts/auth/context";
import { applyProfileLanguagePreferences } from "@/utils/language-preference";

const AUTH_ACCOUNT_DISABLED = "AUTH_ACCOUNT_DISABLED";

interface RegisterResult {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  teams: Array<{ id: string; name: string; role: string }>;
  isNew: boolean;
  onboardingCompleted?: boolean;
}

const DEBUG_AUTH = import.meta.env.DEV ||
  import.meta.env.VITE_DEBUG_AUTH === "true";

export function debugLog(message: string, ..._args: unknown[]) {
  if (DEBUG_AUTH) {
    console.debug(`Auth context: ${message}`);
  }
}

function readViteEnvVar(key: string): string | undefined {
  const env = import.meta.env as Record<string, unknown>;
  const value = env[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function isAccountDisabledError(data: unknown): boolean {
  if (typeof data !== "object" || data === null) return false;
  const obj = data as Record<string, unknown>;
  return obj.code === AUTH_ACCOUNT_DISABLED;
}

export function getApiBase(): string {
  const base = readViteEnvVar("VITE_API_BASE");
  if (base) return base;
  const baseUrl = readViteEnvVar("VITE_API_BASE_URL");
  if (baseUrl) return baseUrl.replace(/\/api$/, "");
  return "http://localhost:8000";
}

export function getCsrfHeaders(): HeadersInit {
  // pirx-i9s6p: prefer the explicitly delivered session token (cross-origin
  // deployment where the CSRF cookie is host-only on the API origin); the
  // helper falls back to the readable cookie on same-origin builds.
  const csrfToken = getCsrfToken();
  return csrfToken ? { "x-csrf-token": csrfToken } : {};
}

export async function exposeDevAuthToken(firebaseUser: User): Promise<void> {
  if (
    typeof window === "undefined" ||
    import.meta.env.MODE !== "development" ||
    import.meta.env.DEV !== true
  ) {
    return;
  }
  try {
    const token = await firebaseUser.getIdToken();
    (window as Window & { __PIRX_AUTH_TOKEN__?: string }).__PIRX_AUTH_TOKEN__ =
      token;
    console.warn(
      "Dev only: auth token exposed to window.__PIRX_AUTH_TOKEN__; do not use in production",
    );
    debugLog("Exposed auth token to window.__PIRX_AUTH_TOKEN__");
  } catch {
    console.warn("Failed to expose auth token for dev tools");
  }
}

export function clearDevAuthToken(): void {
  if (typeof window !== "undefined" && import.meta.env.MODE !== "production") {
    delete (window as Window & { __PIRX_AUTH_TOKEN__?: string })
      .__PIRX_AUTH_TOKEN__;
  }
}

export async function fetchProfileForUser(
  firebaseUser: User,
  setAccountDisabled: (value: boolean) => void,
  // Name the user typed at registration. A pending sign-up may not have
  // propagated displayName to Firebase yet, so the auto-register must not
  // rely on firebaseUser.displayName alone.
  displayNameOverride?: string,
  // Self-reported signup source (GTM plan F2), same lifecycle as the name.
  signupSourceOverride?: string,
): Promise<UserProfile | null> {
  debugLog("fetchProfile called", { email: firebaseUser.email });
  try {
    const apiBase = getApiBase();
    debugLog("Using API base", { apiBase });

    const fetchProfileWithToken = async (token: string) => {
      debugLog("Fetching profile from /api/auth/me");
      return await fetch(`${apiBase}/api/auth/me`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${token}` },
      });
    };

    const establishSession = async (token: string) => {
      const sessionResponse = await fetch(`${apiBase}/api/auth/session`, {
        method: "POST",
        credentials: "include",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!sessionResponse.ok) {
        console.warn(
          "Failed to establish cookie session:",
          sessionResponse.status,
        );
        return;
      }
      // pirx-i9s6p: the CSRF cookie is host-only on the API origin, so a
      // deployed (cross-origin) frontend cannot read it from
      // `document.cookie`. Retain the explicitly delivered token in
      // `sessionStorage` so protected writes can send the `x-csrf-token`
      // header; the server still cross-checks it against the cookie and
      // the signed session, so nothing weakens.
      const sessionData = await sessionResponse.json().catch(() => null);
      const csrfToken =
        sessionData &&
        typeof (sessionData as { csrfToken?: unknown }).csrfToken === "string"
          ? (sessionData as { csrfToken: string }).csrfToken
          : undefined;
      if (csrfToken) {
        storeCsrfToken(csrfToken);
      }
    };

    const registerWithToken = async (token: string) => {
      debugLog("Registering user via /api/auth/register");
      const registerResponse = await fetch(`${apiBase}/api/auth/register`, {
        method: "POST",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          ...getCsrfHeaders(),
        },
        // Omit null identity fields. Firebase returns null displayName/photoURL
        // for photo-less (email/password) accounts; JSON.stringify drops keys
        // whose value is undefined, so `?? undefined` sends neither key rather
        // than `null` (the backend registerSchema rejects null). See pirx-ud4u.
        // displayNameOverride wins: at sign-up it holds the name the user
        // entered even when Firebase has not propagated it yet.
        body: JSON.stringify({
          name: displayNameOverride ?? firebaseUser.displayName ?? undefined,
          avatarUrl: firebaseUser.photoURL ?? undefined,
          // GTM plan F1/F2: first touch, stored on the user row at insert
          // time. Absent for accounts created inside the app.
          signupSource: signupSourceOverride ?? undefined,
          signupAttribution: signupAttributionForRequest() ?? undefined,
        }),
      });
      if (!registerResponse.ok) {
        // Surface loudly: a swallowed failure here strands the user with a
        // Firebase account but no app-DB row and no visible error (pirx-ud4u).
        console.error(
          "Registration failed while creating the account:",
          registerResponse.status,
        );
        return null;
      }
      debugLog("Registration successful");
      await establishSession(token);
      // F1's observable done-when: the registration event carries the
      // campaign and the self-reported source. Read before clearing so the
      // event and the stored row never disagree.
      const attribution = signupAttributionForRequest();
      await trackRegistration({
        signup_source: signupSourceOverride ?? null,
        ...attribution,
      });
      clearStoredAttribution(window.localStorage);
      const registerResult = (await registerResponse.json()) as RegisterResult;
      return {
        id: registerResult.id,
        email: registerResult.email,
        name: registerResult.name,
        avatarUrl: registerResult.avatarUrl,
        teams: registerResult.teams,
        onboardingCompleted: registerResult.onboardingCompleted ?? false,
      };
    };

    const token = await firebaseUser.getIdToken();
    debugLog("Got Firebase ID token", { tokenLength: token.length });
    const response = await fetchProfileWithToken(token);
    debugLog("Profile fetch response", { status: response.status });

    if (response.ok) {
      await establishSession(token);
      const profile = (await response.json()) as UserProfile;
      debugLog("Profile fetched successfully", { userId: profile.id });
      setAccountDisabled(false);
      applyProfileLanguagePreferences(profile);
      return profile;
    }
    if (response.status === 404) {
      debugLog("User not found (404), auto-registering");
      return await registerWithToken(token);
    }
    if (response.status === 403) {
      try {
        const errorData: unknown = await response.json();
        if (isAccountDisabledError(errorData)) {
          debugLog("Account disabled (403), pending admin approval");
          setAccountDisabled(true);
          return null;
        }
      } catch {
        debugLog("Failed to parse 403 response");
      }
    }
    if (response.status === 401) {
      debugLog("Got 401, refreshing token and retrying");
      const refreshedToken = await firebaseUser.getIdToken(true);
      const retryResponse = await fetchProfileWithToken(refreshedToken);
      debugLog("Retry response", { status: retryResponse.status });
      if (retryResponse.ok) {
        await establishSession(refreshedToken);
        const profile = (await retryResponse.json()) as UserProfile;
        debugLog("Profile fetched on retry", { userId: profile.id });
        setAccountDisabled(false);
        applyProfileLanguagePreferences(profile);
        return profile;
      }
      if (retryResponse.status === 404) {
        debugLog("User not found on retry, auto-registering");
        return await registerWithToken(refreshedToken);
      }
      if (retryResponse.status === 403) {
        // Same disabled-account rule as the initial 403 above: a pending
        // approval must surface even when reached via the token-refresh
        // path, otherwise the user is left without the pending-approval
        // state and can only log out and log back in.
        try {
          const retryError: unknown = await retryResponse.json();
          if (isAccountDisabledError(retryError)) {
            debugLog("Account disabled on retry (403), pending admin approval");
            setAccountDisabled(true);
            return null;
          }
        } catch {
          debugLog("Failed to parse retry 403 response");
        }
      }
      console.warn(
        "Authentication failed while fetching profile:",
        retryResponse.status,
      );
    }
    debugLog("fetchProfile returning null", { status: response.status });
    return null;
  } catch {
    console.error("Failed to fetch user profile");
    debugLog("fetchProfile error");
    return null;
  }
}
